import React, { useState } from 'react';

import { Building, ChevronLeft, ChevronRight, Info, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import useGetRelativityFieldMapping from '@/api/queries/useGetRelativityFieldMapping';
import useGetRelativityWorkspaces from '@/api/queries/useGetRelativityWorkspaces';
import { RelativityWorkspaceType } from '@/api/types/backend-dto-types';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { DialogHeader, DialogTitle, DialogDescription } from '@/new/components/shadcn/dialog';
import { Input } from '@/new/components/shadcn/input';
import { Skeleton } from '@/new/components/shadcn/skeleton';

interface RelativityWorkspacesModalProps {
  onSelectWorkspace: (workspace: RelativityWorkspaceType) => void;
  onClose: () => void;
  linkedWorkspaceId?: number | null;
}

interface WorkspaceItemProps {
  workspace: RelativityWorkspaceType;
  isSelected: boolean;
  isDisabled: boolean;
  onSelect: (workspace: RelativityWorkspaceType) => void;
}

const WorkspaceItem: React.FC<WorkspaceItemProps> = ({ workspace, isSelected, isDisabled, onSelect }) => {
  return (
    <div
      className={`group rounded-md border p-2.5 transition-all duration-200 ${
        isDisabled
          ? 'cursor-not-allowed border-gray-200 opacity-50'
          : isSelected
            ? 'border-brandSecondary bg-brandSecondary/10 ring-brandSecondary cursor-pointer'
            : 'hover:border-brandSecondary/30 hover:bg-brandSecondary/5 cursor-pointer border-gray-200'
      }`}
      onClick={() => !isDisabled && onSelect(workspace)}
    >
      <div className="flex min-w-0 flex-1 items-center space-x-2.5">
        <div
          className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md ${
            isSelected ? 'bg-brandSecondary/20' : 'group-hover:bg-brandSecondary/15 bg-gray-100'
          }`}
        >
          <Building
            className={`h-4 w-4 ${
              isSelected ? 'text-brandSecondary' : 'group-hover:text-brandSecondary text-gray-600'
            }`}
          />
        </div>
        <div className="min-w-0 flex-1">
          <h3
            className={`truncate text-sm font-medium ${
              isSelected ? 'text-brandPrimary' : 'group-hover:text-brandPrimary text-gray-900'
            }`}
            title={workspace.name}
          >
            {workspace.name}
          </h3>
          <p className="truncate text-xs text-gray-500" title={`ID: ${workspace.artifactId}`}>
            ID: {workspace.artifactId}
          </p>
        </div>
      </div>
    </div>
  );
};

export const RelativityWorkspacesModal: React.FC<RelativityWorkspacesModalProps> = ({
  onSelectWorkspace,
  linkedWorkspaceId,
}) => {
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedWorkspace, setSelectedWorkspace] = useState<RelativityWorkspaceType | null>(null);
  const pageSize = 5;

  // Note: Search is immediate for better UX, but could be debounced if needed

  const {
    data: workspacesData,
    isLoading,
    error,
  } = useGetRelativityWorkspaces({
    page: currentPage,
    pageSize,
    search: searchQuery,
  });

  // Check if selected workspace has field mappings configured
  const { data: fieldMapping } = useGetRelativityFieldMapping({
    workspaceId: selectedWorkspace?.artifactId ?? null,
    enabled: !!selectedWorkspace,
  });

  const handleSelectWorkspace = (workspace: RelativityWorkspaceType) => {
    setSelectedWorkspace(workspace);
  };

  const handleConfirmSelection = () => {
    if (selectedWorkspace) {
      onSelectWorkspace(selectedWorkspace);
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(1); // Reset to first page when searching
    setSelectedWorkspace(null); // Clear selection when searching
  };

  const totalPages = workspacesData?.totalPages || 0;
  const canPrevious = currentPage > 1;
  const canNext = currentPage < totalPages;

  return (
    <div>
      <DialogHeader>
        <DialogTitle>
          <Icon className="lucide--workflow" />
          Select Relativity Workspace
        </DialogTitle>
        <DialogDescription>Choose a workspace to import documents from Relativity.</DialogDescription>
      </DialogHeader>

      {/* Workspace Restriction Banner */}
      {linkedWorkspaceId && (
        <div className="mb-4 rounded-md border border-amber-300 bg-amber-50 p-3">
          <div className="flex items-start gap-2">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <Info className="size-3 flex-shrink-0 text-amber-700" />

                <p className="text-xs text-amber-700">
                  This matter is already linked to a Relativity workspace. Documents can only be imported from that
                  workspace.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Search and Pagination */}
      <div className="mb-4 flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <Input
            type="text"
            placeholder="Search workspaces..."
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Inline Pagination */}
        {workspacesData && totalPages > 1 && (
          <div className="flex items-center gap-1">
            <button
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={!canPrevious}
              className="flex h-8 w-8 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-400 transition-colors hover:bg-gray-50 hover:text-gray-600 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-white disabled:hover:text-gray-400"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="px-2 text-sm text-gray-600">
              {currentPage} / {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={!canNext}
              className="flex h-8 w-8 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-400 transition-colors hover:bg-gray-50 hover:text-gray-600 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-white disabled:hover:text-gray-400"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>

      <div className="min-h-[280px]">
        {isLoading && (
          <div className="space-y-2" aria-busy="true" aria-label="Loading workspaces">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex items-center rounded-md border border-gray-200 p-2.5">
                <div className="flex min-w-0 flex-1 items-center space-x-2.5">
                  <Skeleton className="h-8 w-8 flex-shrink-0 rounded-md" />
                  <div className="min-w-0 flex-1 space-y-1.5">
                    <Skeleton className="h-4 w-3/4 max-w-[180px] rounded" />
                    <Skeleton className="h-3 w-1/2 max-w-[100px] rounded" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {!!error && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3">
            <div className="flex items-center">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Failed to load workspaces</h3>
                <p className="mt-1 text-sm text-red-700">
                  {error instanceof Error ? error.message : 'An unknown error occurred'}
                </p>
              </div>
            </div>
          </div>
        )}

        {workspacesData && workspacesData.workspaces.length === 0 && !isLoading && (
          <div className="py-12 text-center">
            <Building className="mx-auto h-10 w-10 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              {searchQuery ? 'No matching workspaces' : 'No workspaces found'}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchQuery ? 'Try adjusting your search terms.' : "You don't have access to any Relativity workspaces."}
            </p>
          </div>
        )}

        {workspacesData && workspacesData.workspaces.length > 0 && (
          <div className="space-y-2">
            {workspacesData.workspaces.map((workspace) => (
              <WorkspaceItem
                key={workspace.artifactId}
                workspace={workspace}
                isSelected={selectedWorkspace?.artifactId === workspace.artifactId}
                isDisabled={
                  linkedWorkspaceId !== null &&
                  linkedWorkspaceId !== undefined &&
                  workspace.artifactId !== linkedWorkspaceId
                }
                onSelect={handleSelectWorkspace}
              />
            ))}
          </div>
        )}
      </div>

      {/* Metadata Extraction Status Banner */}
      {selectedWorkspace && fieldMapping && (
        <div className="animate-in fade-in mt-4 rounded-md border border-green-300 bg-green-50 p-3 duration-100">
          <div className="flex items-start gap-2">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <Icon className="lucide--check-circle-2 size-3 flex-shrink-0 text-green-800" />
                <div className="flex gap-1">
                  <p className="text-xs font-medium text-green-800">Metadata extraction enabled:</p>
                  <span className="text-xs text-green-800">
                    Document fields will be automatically mapped during import.
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedWorkspace && !fieldMapping && (
        <div className="animate-in fade-in mt-4 rounded-md border border-blue-300 bg-blue-50 p-3 duration-200">
          <div className="flex items-start gap-2">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <Info className="lucide--alert-circle size-3.5 text-blue-800" />
                <div className="flex gap-1">
                  <p className="text-xs font-medium text-blue-800">No metadata mapping configured:</p>
                  <span className="text-xs text-blue-700">
                    Documents will be imported without metadata extraction. Configure field mappings in your{' '}
                    {t('organisation')} settings to enable this feature.
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="mt-4 flex items-center justify-between border-t border-gray-100 pt-4">
        {selectedWorkspace ? (
          <div className="animate-in fade-in flex w-full items-center justify-between duration-200">
            <div className="text-sm text-gray-500">
              <span className="font-medium text-gray-700">
                Selected Workspace: <span className="font-semibold text-gray-700">{selectedWorkspace.name}</span>
              </span>
            </div>
            <div className="flex justify-end">
              <Button variant="outline" onClick={handleConfirmSelection}>
                Next
                <Icon className="lucide--arrow-right size-4" />
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div className="text-sm text-gray-500" />
            <div className="flex justify-end" />
          </>
        )}
      </div>
    </div>
  );
};
